/**
 * SDK 日期工具(无依赖)
 *
 * 镜像 hotel-hub 用到的 date-fns 子集,避免 date-fns/locale barrel 进 SDK bundle。
 * 逻辑与 hotel-hub 保持一致(尤其是 toLocalDateString 与 readCachedDates 的解析契约)。
 */

/** 格式化为本地日期字符串 (yyyy-MM-dd 00:00:00),与 hotel-hub utils.toLocalDateString 一致 */
export function toLocalDateString(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d} 00:00:00`;
}

/** 格式化为 yyyy-MM-dd(用于 URL 参数与日历展示) */
export function formatYMD(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** 格式化为 yyyy/MM/dd(用于埋点事件值,如切换日期:2026/07/01~2026/07/02) */
export function formatYMDSlash(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}/${m}/${d}`;
}

/** 格式化为 M-d(无前导零、不含年份),用于日期栏 pill 紧凑展示(PC/H5 统一去年) */
export function formatMD(date: Date): string {
  return `${date.getMonth() + 1}-${date.getDate()}`;
}

/**
 * 解析日期字符串,兼容 yyyy-MM-dd 与 yyyy-MM-dd HH:mm:ss。
 * 用 new Date(year, month-1, day) 构造,避免时区漂移(与 readCachedDates 一致按本地日历日)。
 * 非法返回 null。
 */
export function parseDate(input: string): Date | null {
  if (!input) return null;
  const m = /^(\d{4})-(\d{1,2})-(\d{1,2})/.exec(input);
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]) - 1;
  const d = Number(m[3]);
  if (Number.isNaN(y) || Number.isNaN(mo) || Number.isNaN(d)) return null;
  const dt = new Date(y, mo, d);
  if (dt.getFullYear() !== y || dt.getMonth() !== mo || dt.getDate() !== d) return null;
  return dt;
}

/** 两个日期相差的日历天数(b - a,本地日历日归零) */
export function differenceInCalendarDays(a: Date, b: Date): number {
  const da = new Date(a.getFullYear(), a.getMonth(), a.getDate()).getTime();
  const db = new Date(b.getFullYear(), b.getMonth(), b.getDate()).getTime();
  return Math.round((db - da) / 86400000);
}

/** 中文周几:"周一".."周日" */
export function getWeekdayZh(date: Date): string {
  return ['周日', '周一', '周二', '周三', '周四', '周五', '周六'][date.getDay()];
}

/** 归零到本地日历日 00:00:00 */
export function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/** 加 n 个月(保留日,溢出由 Date 自动规整) */
export function addMonths(date: Date, n: number): Date {
  const d = new Date(date);
  d.setMonth(d.getMonth() + n);
  return d;
}

/** 是否同一天(本地日历日) */
export function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}
